// context/SkillContext.jsx - GraphQL Integrated Version
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useAuth } from './AuthContext';

// GraphQL Queries
const GET_MY_SKILL_VERIFICATIONS = gql`
  query GetMySkillVerifications {
    mySkillVerifications {
      id
      skillId
      skillName
      skillCategory
      skillTier
      status
      tokenReward
      tokensAwarded
      submittedAt
      reviewedAt
      reviewNotes
      rejectionReason
      verificationData {
        experienceYears
        description
        documents {
          type
          url
          fileName
        }
        references {
          name
          email
          relationship
        }
      }
    }
  }
`;

const SUBMIT_SKILL_VERIFICATION = gql`
  mutation SubmitSkillVerification($input: SubmitSkillVerificationInput!) {
    submitSkillVerification(input: $input) {
      id
      skillId
      skillName
      skillCategory
      skillTier
      status
      tokenReward
      tokensAwarded
      submittedAt
    }
  }
`;

const SkillContext = createContext({
  userSkills: null,
  skills: [],
  loading: false,
  error: null,
  verifySkill: () => {},
  getSkillVerificationStatus: () => {},
  getSkillsByCategory: () => {},
  meetsSkillRequirements: () => {},
  refreshSkills: () => {},
});

export const useSkills = () => useContext(SkillContext);

export const SkillProvider = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  
  const [error, setError] = useState(null);

  // Predefined skills database (same as before - could be moved to backend later)
  const skills = useMemo(() => [
    // Food & Safety
    {
      id: 'butcher_cert',
      name: 'Professional Butcher',
      category: 'Food & Safety',
      tier: 3,
      description: 'Licensed professional butcher with commercial experience',
      requirements: ['Professional license', 'Health certificate', '2+ years experience'],
      trustRequirement: 4.0,
      tokenReward: 50
    },
    {
      id: 'food_handler',
      name: 'Food Handler Certificate',
      category: 'Food & Safety',
      tier: 2,
      description: 'Certified food handler for safe food preparation',
      requirements: ['Food handler certificate', 'Health department approval'],
      trustRequirement: 3.5,
      tokenReward: 20
    },
    {
      id: 'home_cook',
      name: 'Experienced Home Cook',
      category: 'Food & Safety',
      tier: 1,
      description: 'Experienced in cooking for groups',
      requirements: ['Self-reported experience', 'References optional'],
      trustRequirement: 3.0,
      tokenReward: 10
    },
    
    // Transportation
    {
      id: 'cdl_driver',
      name: 'CDL Driver',
      category: 'Transportation',
      tier: 3,
      description: 'Commercial driver\'s license holder',
      requirements: ['Valid CDL', 'Clean driving record', 'Insurance'],
      trustRequirement: 4.0,
      tokenReward: 40
    },
    {
      id: 'delivery_driver',
      name: 'Delivery Driver',
      category: 'Transportation',
      tier: 2,
      description: 'Experienced delivery driver with vehicle',
      requirements: ['Valid license', 'Vehicle registration', 'Insurance'],
      trustRequirement: 3.5,
      tokenReward: 25
    },
    
    // Organization
    {
      id: 'event_coordinator',
      name: 'Event Coordinator',
      category: 'Organization',
      tier: 2,
      description: 'Experience organizing group events',
      requirements: ['Portfolio or references', 'Example events'],
      trustRequirement: 3.5,
      tokenReward: 30
    },
    {
      id: 'inventory_mgmt',
      name: 'Inventory Management',
      category: 'Organization',
      tier: 2,
      description: 'Experience with inventory and distribution',
      requirements: ['Work history', 'System knowledge'],
      trustRequirement: 3.5,
      tokenReward: 25
    },
    
    // Facilities
    {
      id: 'commercial_kitchen',
      name: 'Commercial Kitchen Access',
      category: 'Facilities',
      tier: 3,
      description: 'Access to licensed commercial kitchen',
      requirements: ['Kitchen license', 'Health permits', 'Availability schedule'],
      trustRequirement: 4.0,
      tokenReward: 45
    },
    {
      id: 'storage_facility',
      name: 'Storage Facility',
      category: 'Facilities',
      tier: 2,
      description: 'Climate-controlled storage space',
      requirements: ['Facility photos', 'Capacity details', 'Access hours'],
      trustRequirement: 3.5,
      tokenReward: 35
    }
  ], []);

  // GraphQL Query for user's skill verifications
  const { 
    data: verificationsData, 
    loading: verificationsLoading,
    refetch: refetchVerifications 
  } = useQuery(GET_MY_SKILL_VERIFICATIONS, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network'
  });

  // GraphQL Mutation for submitting skill verification
  const [submitVerificationMutation, { loading: submitting }] = useMutation(SUBMIT_SKILL_VERIFICATION, {
    refetchQueries: [{ query: GET_MY_SKILL_VERIFICATIONS }]
  });

  // Process user skills from verifications
  const userSkills = useMemo(() => {
    const verifications = verificationsData?.mySkillVerifications || [];
    
    const verified = verifications
      .filter(v => v.status === 'approved')
      .map(v => ({
        id: v.id,
        skillId: v.skillId,
        name: v.skillName,
        category: v.skillCategory,
        tier: v.skillTier,
        status: 'verified',
        verifiedDate: v.reviewedAt,
        tokenReward: v.tokenReward
      }));
    
    const pending = verifications
      .filter(v => v.status === 'pending' || v.status === 'under_review')
      .map(v => ({
        id: v.id,
        skillId: v.skillId,
        name: v.skillName,
        category: v.skillCategory,
        tier: v.skillTier,
        status: 'pending',
        submittedDate: v.submittedAt
      }));
    
    const rejected = verifications
      .filter(v => v.status === 'rejected')
      .map(v => ({
        id: v.id,
        skillId: v.skillId,
        name: v.skillName,
        category: v.skillCategory,
        tier: v.skillTier,
        status: 'rejected',
        rejectionReason: v.rejectionReason
      }));
    
    return {
      verified,
      pending,
      rejected,
      selfReported: [] // Keep for compatibility
    };
  }, [verificationsData]);

  // Verify a skill (submit for verification)
  const verifySkill = useCallback(async (skillId, verificationData) => {
    setError(null);
    
    try {
      const skill = skills.find(s => s.id === skillId);
      if (!skill) throw new Error('Skill not found');
      
      const input = {
        skillId: skill.id,
        skillName: skill.name,
        skillCategory: skill.category,
        skillTier: skill.tier,
        tokenReward: skill.tokenReward,
        experienceYears: verificationData.experienceYears || null,
        description: verificationData.description || null,
        documents: (verificationData.documents || []).map(doc => ({
          type: doc.type || 'other',
          url: doc.url || '',
          fileName: doc.fileName || ''
        })),
        references: (verificationData.references || []).map(ref => ({
          name: ref.name || '',
          email: ref.email || '',
          relationship: ref.relationship || ''
        }))
      };

      const { data } = await submitVerificationMutation({
        variables: { input }
      });
      
      return {
        id: data.submitSkillVerification.id,
        skillId: skill.id,
        name: skill.name,
        category: skill.category,
        tier: skill.tier,
        status: data.submitSkillVerification.status === 'approved' ? 'verified' : 'pending',
        submittedDate: data.submitSkillVerification.submittedAt
      };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [skills, submitVerificationMutation]);

  // Get verification status for a skill
  const getSkillVerificationStatus = useCallback((skillId) => {
    const verified = userSkills.verified.find(s => s.skillId === skillId);
    if (verified) return 'verified';
    
    const pending = userSkills.pending.find(s => s.skillId === skillId);
    if (pending) return 'pending';
    
    const rejected = userSkills.rejected.find(s => s.skillId === skillId);
    if (rejected) return 'rejected';
    
    return 'unverified';
  }, [userSkills]);

  // Get skills by category
  const getSkillsByCategory = useCallback((category) => {
    if (!category) return skills;
    return skills.filter(s => s.category === category);
  }, [skills]);

  // Check if user meets requirements for a skill
  const meetsSkillRequirements = useCallback((skillId) => {
    const skill = skills.find(s => s.id === skillId);
    if (!skill) return false;
    
    // Simplified trust check - in production, get actual trust score
    const userTrustScore = user?.trust?.score || 3.0;
    return userTrustScore >= skill.trustRequirement;
  }, [skills, user]);

  // Refresh skills from backend
  const refreshSkills = useCallback(() => {
    refetchVerifications();
  }, [refetchVerifications]);

  const value = {
    userSkills,
    skills,
    loading: verificationsLoading || submitting,
    error,
    verifySkill,
    getSkillVerificationStatus,
    getSkillsByCategory,
    meetsSkillRequirements,
    refreshSkills
  };

  return (
    <SkillContext.Provider value={value}>
      {children}
    </SkillContext.Provider>
  );
};

// Export skillCategories for use in other components
export const skillCategories = ['Food & Safety', 'Transportation', 'Organization', 'Facilities'];
